import { getGenerativeModel } from 'firebase/ai';
import { vertexAI } from '../lib/firebase';

export interface DeepAnalysisResult {
    criticalRisks: string[];
    complianceChecklist: string[];
    hiddenOpportunities: string[];
    technicalStack: string[];
    executiveSummary: string;
}

// Client-side Prompt for Opportunity Analysis (Deep Think)
const OPPORTUNITY_PROMPT = `
You are a strategic government contract analyst. Analyze this opportunity for a SPECIFIC contractor.

## Contractor Profile
Name: {{CONTRACTOR_NAME}}
Core Capabilities (Keywords): {{KEYWORDS}}
Primary NAICS: {{CONTRACTOR_NAICS}}
Capability Statement: {{CAPABILITY_STATEMENT}}

## Opportunity Details
Title: {{TITLE}}
Agency: {{AGENCY}}
NAICS: {{NAICS_REQ}}
Set Aside: {{SET_ASIDE}}
Description: {{DESCRIPTION}}

## Task: Deep Fit Analysis
1. **Fit Score (0-100):** How well does the Contractor's profile match the Opportunity's requirements?
2. **Opportunity Score (0-100):** Strategic value (Low barrier, High growth potential = higher score).
3. **AI Executive Summary:** A harsh, realistic mission-focused assessment. 
   - Start with "🔥 STRONG BUY", "✅ CONSIDER", or "❌ PASS".
   - Briefly explain the primary "Killer Factor".
4. **Actions:** 3 specific, non-generic next steps. 
   - Good: "Research incumbent CACI's recent task orders on this vehicle."
   - Bad: "Review the documents."

Output JSON:
{
  "fitScore": number,
  "opportunityScore": number,
  "aiSummary": "string",
  "actions": ["string", "string", "string"]
}
`;

// Helper for robustness
async function retry<T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        if (retries <= 0) throw error;
        await new Promise(resolve => setTimeout(resolve, delay));
        return retry(fn, retries - 1, delay * 2);
    }
}

export const analyzeOpportunity = async (contract: any, contractor?: any): Promise<any> => {
    try {
        const model = getGenerativeModel(vertexAI, {
            model: 'gemini-2.0-flash',
            generationConfig: { responseMimeType: 'application/json' }
        });

        // Fallback if no contractor provided (Generic Analysis)
        const contractorName = contractor?.displayName || "Generic Consultant";
        const contractorKeywords = contractor?.keywords?.join(', ') || "General Services";
        const contractorNaics = contractor?.naicsCodes?.join(', ') || "Various";
        const capStatement = contractor?.capabilityStatement || "Not provided";

        const prompt = OPPORTUNITY_PROMPT
            .replace('{{CONTRACTOR_NAME}}', contractorName)
            .replace('{{KEYWORDS}}', contractorKeywords)
            .replace('{{CONTRACTOR_NAICS}}', contractorNaics)
            .replace('{{CAPABILITY_STATEMENT}}', capStatement)
            .replace('{{TITLE}}', contract.title || 'Unknown')
            .replace('{{AGENCY}}', contract.agency || 'Unknown')
            .replace('{{DESCRIPTION}}', contract.description?.substring(0, 5000) || 'No description provided')
            .replace('{{NAICS_REQ}}', contract.naicsCode || 'Unknown')
            .replace('{{SET_ASIDE}}', contract.type || 'Open');

        // Robust AI Execution with Retry
        const result = await retry(async () => {
            console.log("Attempting AI generation...");
            return await model.generateContent(prompt);
        }, 3, 2000);

        const response = await result.response;
        const responseText = response.text();

        console.log("Gemini Raw Response:", responseText); // Debugging

        const analysis = JSON.parse(responseText);

        return {
            ...analysis,
            isAnalyzed: true
        };
    } catch (error) {
        console.error('Client-side AI Analysis failed after retries:', error);
        // Fallback only if the API call itself crashes (e.g. network)
        return {
            fitScore: 0,
            opportunityScore: 0,
            aiSummary: "AI Analysis currently unavailable (Network/API Error). Please retry.",
            actions: ["Retry Analysis"],
            isAnalyzed: false
        };
    }
};

// Profile Extraction Prompt
const PROFILE_EXTRACTION_PROMPT = `
You are an expert Government Contracting Officer.
Analyze the following company text (Capability Statement or Website Content) and extract a structured profile.

Input Text:
{{TEXT}}

Output JSON Format:
{
  "name": "string (Company Name, infer if missing)",
  "description": "string (Professional summary, max 2 sentences)",
  "naicsCodes": ["string", "string"],
  "keywords": ["string", "string"] (Core Competencies),
  "setAsides": ["string"] (e.g. "Small Business", "WOSB", "SDVOSB", "8(a)", "HUBZone")
}
`;

export const extractContractorProfile = async (text: string): Promise<any> => {
    try {
        const model = getGenerativeModel(vertexAI, {
            model: 'gemini-2.0-flash',
            generationConfig: { responseMimeType: 'application/json' }
        });

        const prompt = PROFILE_EXTRACTION_PROMPT.replace('{{TEXT}}', text.substring(0, 10000));

        const result = await model.generateContent(prompt);
        const responseText = result.response.text();
        console.log("Gemini Profile Extraction:", responseText);

        return JSON.parse(responseText);
    } catch (error) {
        console.error('Profile Extraction Failed:', error);
        throw error;
    }
};

// Firestore Persistence for Analysis
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const ANALYSES_COLLECTION = 'contract_analyses';

export const getStoredAnalysis = async (noticeId: string): Promise<any | null> => {
    try {
        const docRef = doc(db, ANALYSES_COLLECTION, noticeId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() : null;
    } catch (e) {
        console.warn("Failed to fetch stored analysis", e);
        return null;
    }
};

export const saveAnalysis = async (noticeId: string, analysisData: any): Promise<void> => {
    try {
        const docRef = doc(db, ANALYSES_COLLECTION, noticeId);
        await setDoc(docRef, {
            ...analysisData,
            updatedAt: new Date().toISOString()
        }, { merge: true });
    } catch (e) {
        console.error("Failed to save analysis", e);
    }
};

// Deep Analysis currently requires backend PDF parsing. 
// We will placeholder this to prevent errors until backend is fixed or we use a client-side text extractor.
export const analyzeContractDeep = async (_contractId: string, _fileUrls: string[]): Promise<DeepAnalysisResult> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Return a "Not Implemented" state or a mock based on the ID to show UI
    return {
        executiveSummary: "Deep Document Analysis is currently disabled while we upgrade the backend PDF processing engine. Please rely on the Opportunity Scores above.",
        criticalRisks: ["PDF Parsing unavailable"],
        complianceChecklist: ["Verify manually"],
        hiddenOpportunities: ["N/A"],
        technicalStack: ["N/A"]
    };
};
